<script>
export default {
    page() {
        const title = 'Cancel Premium'
        const description = 'Cancel Premium'
        return {
            title,
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },

                { property: 'twitter:title', vmid: 'twitter:title', content: title },
                { property: 'twitter:description', vmid: 'twitter:description', content: description },
            ],
        }
    },
}
</script>

<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router/composables'
import { useStore } from '@composables/use-store'
import { useMessages } from '@composables/use-messages'
import DataLayer from '@services/data-layer/index'
import formatDate from './../../../utils/format-date-full'

const premiumService = DataLayer.subscriptions

const store = useStore()
const router = useRouter()
const getMessageText = useMessages()

const crumbs = [
    { label: 'Account Management', to: '/account' },
    { label: 'Cancel Premium' },
]

const isLoading = ref(false)

const user = computed(() => store.getters['member/currentMember'])
const currentSubscription = computed(() => store.getters['premium/currentSubscription'])

const handleSubscriptionUpdate = async () => {
    const subscriptions = await premiumService.getSubscriptionsByMemberId(user.value.id)
    store.commit('premium/SET_SUBSCRIPTIONS', subscriptions.body)
    const activeSubscription = store.getters['premium/activeSubscription']

    if (activeSubscription) {
        const transaction = await premiumService.getCheckoutById(activeSubscription.subscriptionsCheckoutId.id)
        store.commit('premium/SET_ACTIVE_TRANSACTION', transaction.body)
    }
    else store.commit('premium/SET_ACTIVE_TRANSACTION', null)
}

const cancelSubscription = async () => {
    isLoading.value = true
    try {
        await premiumService.cancelSubscription(currentSubscription.value)
        await handleSubscriptionUpdate()
        router.push('/account/cancellation-feedback')
        isLoading.value = false
    } catch (error) {
        store.dispatch('appearance/handleNotification', {
            message: getMessageText('SOMETHING_WENT_WRONG'),
            type: 'danger',
        })
        isLoading.value = false
    }
}

const onKeepPremium = () => {
    router.push('/account')
}
</script>

<template>
    <div class="cancel-premium-container">
        <BaseBreadcrumbs :crumbs="crumbs"/>
        <div class="main-container text-center">
            <div class="title">Are you sure?</div>
            <div class="subtitle fw-medium">
                If you cancel, you'll change to our free plan on {{ formatDate(new Date(currentSubscription.endTime)) }}.
                <br>
                Here's how your listening will change on that date:
            </div>
            <div class="points">
                <div class="point">
                    <BaseIcon
                        class="point-icon"
                        name="disc"
                    />
                    <div class="point-description fw-medium">No more hi-res audio</div>
                </div>
                <div class="point">
                    <BaseIcon
                        class="point-icon"
                        name="cloud-download"
                    />
                    <div class="point-description fw-medium">
                        No more downloads
                        <br>
                        on our mobile apps to listen offline
                    </div>
                </div>
                <div class="point">
                    <BaseIcon
                        class="point-icon"
                        name="play-reverse"
                    />
                    <div class="point-description fw-medium">No more on-demand playback</div>
                </div>
                <div class="point">
                    <div class="mixes-count-number fw-black">100,000</div>
                    <div class="point-description fw-medium">
                        You'll no longer have instant access to over 100,000
                        <br>
                        exclusive mixes
                    </div>
                </div>
            </div>
            <div class="actions">
                <BaseButton
                    class="action-button"
                    button-type="tagBlack"
                    size="medium"
                    type="button"
                    @click="onKeepPremium"
                >Keep Premium</BaseButton>

                <BaseButton
                    class="action-button"
                    button-type="tagBlack"
                    size="medium"
                    type="button"
                    @click="cancelSubscription"
                >
                    <span class="button-content">Continue to cancel<BaseIcon
                        v-if="isLoading"
                        class="loading-icon spin"
                        name="spinner"
                    /></span>
                </BaseButton>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.cancel-premium-container {
    padding-bottom: $s-h;
}

.main-container {
    margin-top: $s-l;
}

.title {
    @extend %h1;
}

.subtitle {
    font-size: 14px;
    line-height: 150%;
}

.points {
    display: flex;
    flex-direction: column;
    gap: $s-l;
    margin-top: $s-xl;
}

.point {
    display: flex;
    flex-direction: column;
    gap: $s-xs;
    align-items: center;
}

.point-icon {
    max-width: 100px;
    max-height: 100px;
    fill: $c-white;
}

.point-description {
    font-size: 14px;
    line-height: 150%;
}

.mixes-count-number {
    font-size: 48px;
}

.actions {
    display: flex;
    align-items: center;
    justify-content: space-around;
    margin-top: $s-l;

    .action-button {
        padding: $s-m $s-l;
        font-weight: unset;
        text-transform: unset;
    }
}

.button-content {
    display: flex;
    gap: $s-m;
    align-items: center;
}

.loading-icon {
    max-width: 20px;
    max-height: 20px;
    fill: $c-white;
}

@include tablet {
    .subtitle {
        font-size: 16px;
    }

    .points {
        gap: $s-xxl;
        margin-top: $s-h;
    }

    .point {
        gap: $s-s;
    }

    .point-icon {
        max-width: 140px;
        max-height: 140px;
    }

    .point-description {
        font-size: 16px;
    }

    .mixes-count-number {
        font-size: 96px;
    }

    .actions {
        margin-top: $s-xxl;
    }
}
</style>
